import React, { ReactElement, useState, useRef } from "react";
import Draggable from "react-draggable";
import { ModalContainer, ModalBackdrop, styledScrollBars, Centered } from "./StyledComponents";
import styled from "styled-components";
import { EDITBOX_BORDER } from "src/gantt/helpers/Const";
import { DragHandle } from "../filters/FiltersCommon";
import {  GenericButton } from "./Buttons";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { iRootState, Dispatch } from "src/rematch/store";
import { TextInput } from "../login/LoginComponents";
import { loginToMfilesAsync } from "src/backend/configuration/authentication";
import { IAuthenticationInformation } from "src/backend/interfaces/configuration/IAuthenticationInformation";
import { QUERYSTRING_TOKEN_PART } from "src/backend/constants/common";
import { addQueryParameter } from "src/backend/helpers/urlHelper";
import { parseDate, formatWithLocale } from "src/gantt/helpers/DateHelpers";
import { addMonths } from "date-fns/esm";

const TOKENFORM_MODAL_WIDTH = 460;
const TOKENFORM_MODAL_HEIGHT = 500;

const TOKENFORM_MODAL_PADDING = 24;
const TOKENFORM_MODAL_HEADER_HEIGHT = 40;
const TOKENFORM_MODAL_FOOTER_HEIGHT = 62;
const TOKENFORM_MODAL_CONTENT_HEIGHT = TOKENFORM_MODAL_HEIGHT - TOKENFORM_MODAL_HEADER_HEIGHT - TOKENFORM_MODAL_FOOTER_HEIGHT - 2 * TOKENFORM_MODAL_PADDING;

const DRAG_HANDLE_CLASSNAME = "dragHandle";

const TokenPage = styled.div`
  border: 2px solid ${EDITBOX_BORDER};
  background: white;
  padding:  ${TOKENFORM_MODAL_PADDING}px;
  width: ${TOKENFORM_MODAL_WIDTH}px;
  height: ${TOKENFORM_MODAL_HEIGHT}px;
`;

const TokenHeaderText = styled.div`
  font-weight: bold;
  font-size: 20px;
  margin-bottom: 20px;
`;

const TokenHeader = styled.div`
  height: ${TOKENFORM_MODAL_HEADER_HEIGHT}px;
`;

const TokenContent = styled.div`
  height: ${TOKENFORM_MODAL_CONTENT_HEIGHT}px;
  display: flex;
  flex-direction: column;
  overflow: auto;
  ${styledScrollBars}
`;

const TokenMessage = styled.div`
  margin-top: 20px;
`;

const TokenFooter = styled.div`
  height: ${TOKENFORM_MODAL_FOOTER_HEIGHT}px;
  padding-top: 20px;
`;

export const TokenForm = (): ReactElement => {

  const { t } = useTranslation();
  const dispatch = useDispatch<Dispatch>();
  const vaultGuid = useSelector((state: iRootState) => state.common.configuration.vaultGuid);
  const mfilesApiBaseUrl = useSelector((state: iRootState) => state.common.configuration.mfilesApiBaseUrl);
  const dateFormat = useSelector((state: iRootState) => state.UI.displaySettings.dateFormat);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isWindowsUser, setIsWindowsUser] = useState(false);

  const initialDate = addMonths(new Date(), 1);
  const initialDateStr = formatWithLocale(initialDate, dateFormat);

  const [expiration, setExpiration] = useState(initialDateStr);
  const [expirationDate, setExpirationDate] = useState(initialDate);
  const [message, setMessage] = useState("");
  const [messageColor, setMessageColor] = useState("black");
  const dragRef = useRef<HTMLDivElement>();

  const toggleVisibility = () => {
    dispatch.UI.toggleShowTokenForm();
  }

  const redMessage = (msg: string) => {
    setMessage(msg);
    setMessageColor("red");
  }

  const greenMessage = (msg: string) => {
    setMessage(msg);
    setMessageColor("green");
  }

  const trySetExpiration = (value: string) => {
    setExpiration(value);
    if( value?.length > 0  ) {
      const dateValue = parseDate(value, dateFormat);
      if(dateValue === null || !isFinite(Number(dateValue))) {
        redMessage(t("error.invalidexpirationdate"));
        setExpirationDate(undefined);
      } else {
        greenMessage("");
        setExpirationDate(dateValue);
      }
    } else {
      greenMessage("");
      setExpirationDate(undefined);
    }
  }

  const createToken = async () => {

    if(!expirationDate) {
      redMessage(t("error.expirationdatemandatory"));
      return;
    }

    const authenticationInformation: IAuthenticationInformation = {
      Username: username,
      Password: password,
      VaultGuid: vaultGuid,
      WindowsUser: isWindowsUser,
      ReadOnly: true,
      Expiration: expirationDate,
    };

    try {
      const apiToken = await loginToMfilesAsync(mfilesApiBaseUrl, authenticationInformation);
      const url = addQueryParameter(window.location.href, QUERYSTRING_TOKEN_PART, apiToken);
      navigator.clipboard.writeText(url);
      greenMessage(t("login.tokenclipboard"));
    } catch (err) {
      redMessage(t('error.unknown', { err:  err?.response?.data?.Message }));
    }
  };

  return (
    <div>
      <Draggable nodeRef={dragRef} handle={`.${DRAG_HANDLE_CLASSNAME}`}>
        <ModalContainer ref={dragRef} style={{ top: "50%", marginTop: -TOKENFORM_MODAL_HEIGHT / 2, left: "50%", marginLeft: -TOKENFORM_MODAL_WIDTH / 2 }}>
          <TokenPage>
            <TokenHeader>
              <DragHandle><TokenHeaderText>{t("usermenu.loginlink")}</TokenHeaderText></DragHandle>
            </TokenHeader>
            <TokenContent>
              <TextInput inputType={"text"} label={t("login.username")} value={username} onChange={setUsername} />
              <TextInput inputType={"password"} label={t("login.password")} value={password} onChange={setPassword} />
              <TextInput inputType={"text"} label={t("login.expirationdate")} value={expiration} onChange={trySetExpiration} />
              <div>
                <input
                  type="checkbox"
                  checked={isWindowsUser}
                  id="isWindowsUserCheckbox"
                  onChange={(event) => setIsWindowsUser(event.target.checked)}
                />
                <label htmlFor="isWindowsUserCheckbox">{t("login.windowsuser").toString()}</label>
              </div>
              <TokenMessage style={{color: messageColor}}>{message}</TokenMessage>
            </TokenContent>
            <TokenFooter>
              <Centered>
                <GenericButton disabled={!username || !password || !expirationDate} onClick={createToken} width={136}>
                  {t("command.create")}
                </GenericButton>
              </Centered>
            </TokenFooter>
          </TokenPage>
        </ModalContainer>
      </Draggable>
      <ModalBackdrop onClick={toggleVisibility}></ModalBackdrop>
    </div>
  );

}